import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, AlertTriangle, Loader2, CheckCircle, XCircle, Edit3, X } from 'lucide-react';
import { format, addDays, isBefore, startOfDay, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function ManageBooking() {
  const [searchParams] = useSearchParams();
  const [bookingCode, setBookingCode] = useState(searchParams.get('code') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [booking, setBooking] = useState(null);
  const [settings, setSettings] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [modifyData, setModifyData] = useState({ new_date: null, new_time_slot: '', reason: '' });

  const [syncingPayment, setSyncingPayment] = useState(false);

  // Sync payment status with Stripe
  const syncPaymentStatus = async (sessionId, bookingId) => {
    setSyncingPayment(true);
    try {
      const response = await api.post('/payments/sync-status', {
        session_id: sessionId,
        booking_id: bookingId
      });
      
      if (response.data.status === 'paid') {
        toast.success('Pagamento confermato!');
        // Refresh booking data
        if (bookingCode && email) {
          await performSearch(bookingCode, email);
        }
      } else if (response.data.status === 'unpaid') {
        toast.info('Pagamento in elaborazione...');
        // Retry after 3 seconds
        setTimeout(() => syncPaymentStatus(sessionId, bookingId), 3000);
        return; // Don't clear syncing state yet
      }
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncingPayment(false);
    }
  };

  useEffect(() => {
    api.get('/public/settings').then(res => setSettings(res.data)).catch(console.error);
    api.get('/public/availability').then(res => setAvailability(res.data)).catch(console.error);

    // Auto-search if params provided
    const code = searchParams.get('code');
    const emailParam = searchParams.get('email');
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (code && emailParam) {
      setBookingCode(code.toUpperCase());
      setEmail(emailParam);
      
      // Delayed search
      setTimeout(async () => {
        await performSearch(code, emailParam);
        
        // If returning from Stripe payment success, sync status
        if (payment === 'success' && sessionId) {
          toast.info('Verifica pagamento in corso...');
          syncPaymentStatus(sessionId, null);
        } else if (payment === 'cancelled') {
          toast.warning('Pagamento annullato');
        }
      }, 100);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const performSearch = async (code, emailVal) => {
    if (!code || !emailVal) return;
    setLoading(true);
    setSearched(true);
    try {
      const response = await api.get(`/bookings/track/${code.toUpperCase()}?email=${encodeURIComponent(emailVal)}`);
      setBooking(response.data);
    } catch (error) {
      setBooking(null);
      toast.error(error.response?.data?.detail || 'Prenotazione non trovata');
    } finally {
      setLoading(false);
    }
  };

  const t = (key, fallback) => settings?.texts?.[key] || fallback;

  // Handle Stripe payment
  const handlePayDeposit = async () => {
    if (!booking) return;
    setPaymentLoading(true);
    try {
      const response = await api.post('/payments/create-checkout', {
        booking_id: booking.id
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.reused) {
        // Session already exists, redirect
        toast.info('Ripresa sessione di pagamento esistente...');
        window.location.href = response.data.url;
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Errore nel pagamento';
      
      if (status === 409) {
        // Conflict - già pagato o checkout in corso
        toast.warning(detail);
        // Refresh booking data
        await performSearch(bookingCode, email);
      } else {
        toast.error(detail);
      }
      setPaymentLoading(false);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    performSearch(bookingCode, email);
  };

  const isDateDisabled = (date) => {
    if (!availability) return true;
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return true;
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    if (!availability.settings?.working_days?.includes(adjustedDay)) return true;
    const dateStr = format(date, 'yyyy-MM-dd');
    if (availability.blocked_dates?.includes(dateStr)) return true;
    if (availability.booked_dates?.includes(dateStr)) return true;
    return false;
  };

  const handleCancellationRequest = async () => {
    if (!cancelReason.trim()) {
      toast.error('Inserisci un motivo');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/bookings/request-modification', {
        booking_code: booking.booking_code,
        email: email,
        request_type: 'cancellation',
        reason: cancelReason
      });
      toast.success('Richiesta di cancellazione inviata');
      setShowCancelModal(false);
      setCancelReason('');
      handleSearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModificationRequest = async () => {
    if (!modifyData.new_date && !modifyData.new_time_slot) {
      toast.error('Seleziona una nuova data o orario');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/bookings/request-modification', {
        booking_code: booking.booking_code,
        email: email,
        request_type: 'modification',
        new_date: modifyData.new_date ? format(modifyData.new_date, 'yyyy-MM-dd') : null,
        new_time_slot: modifyData.new_time_slot || null,
        reason: modifyData.reason
      });
      toast.success('Richiesta di modifica inviata');
      setShowModifyModal(false);
      setModifyData({ new_date: null, new_time_slot: '', reason: '' });
      handleSearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status, paymentStatus) => {
    const badges = {
      in_attesa: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      accettata: { label: 'Accettata', color: 'bg-green-200 text-green-800', icon: CheckCircle },
      rifiutata: { label: 'Rifiutata', color: 'bg-red-100 text-red-700', icon: XCircle },
      completata: { label: 'Completata', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      annullata: { label: 'Annullata', color: 'bg-gray-100 text-gray-700', icon: XCircle }
    };
    const badge = badges[status] || badges.in_attesa;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded ${badge.color}`}>
        <Icon size={16} /> {badge.label}
        {paymentStatus === 'caparra_pagata' && <span className="ml-1 text-xs">(Pagata)</span>}
      </span>
    );
  };

  const canModify = booking && !['annullata', 'completata'].includes(booking.status);
  const canCancel = booking && !['annullata', 'completata'].includes(booking.status);
  const hasPendingRequest = booking?.status === 'modifica_richiesta';

  return (
    <div className="pt-32 pb-24" data-testid="manage-booking-page">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">Gestione</span>
          <h1 className="font-heading text-4xl sm:text-5xl mt-4 mb-4">
            {t('manage_booking_title', 'Gestisci Prenotazione')}
          </h1>
          <p className="text-muted-foreground">
            {t('manage_booking_subtitle', 'Visualizza, modifica o cancella la tua prenotazione')}
          </p>
        </motion.div>

        {/* Search Form */}
        {!booking && (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSearch}
            className="bg-card border border-border p-8 mb-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div>
                <Label className="mb-2 block">Codice Prenotazione</Label>
                <Input
                  value={bookingCode}
                  onChange={(e) => setBookingCode(e.target.value.toUpperCase())}
                  placeholder="Es: ABC12345"
                  className="font-mono uppercase"
                />
              </div>
              <div>
                <Label className="mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Cerca Prenotazione
            </Button>
          </motion.form>
        )}

        {/* Booking Details */}
        {booking && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="bg-card border border-border p-8 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                  <span className="text-sm text-muted-foreground">Codice</span>
                  <h2 className="font-mono text-2xl font-bold">{booking.booking_code}</h2>
                </div>
                {getStatusBadge(booking.status, booking.payment_status)}
              </div>

              <dl className="space-y-4">
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground">Servizio</dt>
                  <dd className="font-medium">{booking.service_type}</dd>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground flex items-center gap-2"><Calendar size={16} /> Data</dt>
                  <dd className="font-medium">{booking.preferred_date}</dd>
                </div>
                {booking.time_slot && (
                  <div className="flex justify-between py-3 border-b border-border">
                    <dt className="text-muted-foreground flex items-center gap-2"><Clock size={16} /> Orario</dt>
                    <dd className="font-medium">{booking.time_slot}</dd>
                  </div>
                )}
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground flex items-center gap-2"><MapPin size={16} /> Luogo</dt>
                  <dd className="font-medium">{booking.location}</dd>
                </div>
                {booking.confirmed_price && (
                  <div className="flex justify-between py-3 border-t border-border">
                    <dt className="text-muted-foreground">Prezzo Confermato</dt>
                    <dd className="font-medium">€{booking.confirmed_price?.toFixed(2)}</dd>
                  </div>
                )}
                {booking.confirmed_price && booking.deposit_amount > 0 && (
                  <div className="flex justify-between py-3">
                    <dt className="text-muted-foreground">Caparra (15%)</dt>
                    <dd className="font-medium">€{booking.deposit_amount?.toFixed(2)} {booking.payment_status === 'caparra_pagata' ? '✓ Pagata' : '- Da pagare'}</dd>
                  </div>
                )}
              </dl>

              {hasPendingRequest && (
                <div className="mt-6 p-4 bg-blue-50 text-blue-700 rounded flex items-start gap-3">
                  <AlertTriangle size={20} />
                  <div>
                    <p className="font-medium">Richiesta in attesa</p>
                    <p className="text-sm">La tua richiesta di modifica/cancellazione è in attesa di approvazione.</p>
                  </div>
                </div>
              )}

              {/* Payment Syncing Indicator */}
              {syncingPayment && (
                <div className="mt-6 p-6 bg-blue-50 border border-blue-200 rounded flex items-center gap-3">
                  <Loader2 size={24} className="animate-spin text-blue-600" />
                  <div>
                    <h3 className="font-heading text-lg text-blue-800">Verifica pagamento in corso...</h3>
                    <p className="text-sm text-blue-600">Stiamo verificando lo stato del tuo pagamento. Attendi qualche secondo.</p>
                  </div>
                </div>
              )}

              {/* Payment Already Processing - caparra_in_attesa */}
              {!syncingPayment && booking.payment_status === 'caparra_in_attesa' && (
                <div className="mt-6 p-6 bg-yellow-50 border border-yellow-200 rounded">
                  <h3 className="font-heading text-lg mb-2 text-yellow-800">Pagamento in elaborazione</h3>
                  <p className="text-sm text-yellow-700 mb-4">
                    Il tuo pagamento è in fase di elaborazione. Se hai già completato il pagamento su Stripe, attendi qualche secondo per l&apos;aggiornamento dello stato.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      if (booking.stripe_session_id) {
                        syncPaymentStatus(booking.stripe_session_id, booking.id);
                      } else {
                        performSearch(bookingCode, email);
                      }
                    }}
                    disabled={syncingPayment}
                  >
                    <Loader2 size={16} className={`mr-2 ${syncingPayment ? 'animate-spin' : ''}`} />
                    Verifica stato pagamento
                  </Button>
                </div>
              )}

              {/* Payment Section - Only show when admin has ACCEPTED and payment not started yet */}
              {!syncingPayment && booking.status === 'accettata' && booking.confirmed_price && booking.deposit_amount > 0 && booking.payment_status !== 'caparra_pagata' && booking.payment_status !== 'caparra_in_attesa' && (
                <div className="mt-6 p-6 bg-primary/5 border border-primary/20 rounded">
                  <h3 className="font-heading text-lg mb-2">Completa il Pagamento</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    La tua richiesta è stata accettata! Il prezzo del servizio è <strong>€{booking.confirmed_price?.toFixed(2)}</strong>.<br/>
                    Versa la caparra di <strong>€{booking.deposit_amount?.toFixed(2)}</strong> (15%) per bloccare definitivamente la data.
                  </p>
                  <Button onClick={handlePayDeposit} disabled={paymentLoading} data-testid="pay-deposit-btn">
                    {paymentLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                    Paga Caparra - €{booking.deposit_amount?.toFixed(2)}
                  </Button>
                </div>
              )}

              {/* Waiting for acceptance - booking not yet accepted */}
              {booking.status !== 'accettata' && booking.status !== 'annullata' && booking.status !== 'rifiutata' && booking.payment_status !== 'caparra_pagata' && (
                <div className="mt-6 p-6 bg-yellow-50 border border-yellow-200 rounded">
                  <h3 className="font-heading text-lg mb-2 text-yellow-800">In Attesa di Accettazione</h3>
                  <p className="text-sm text-yellow-700">
                    La tua richiesta è in fase di valutazione. Riceverai una notifica appena verrà accettata e potrai procedere con il pagamento della caparra.
                  </p>
                </div>
              )}

              {/* CANCELLED BOOKING - Show refund status to customer */}
              {booking.status === 'annullata' && (
                <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded" data-testid="cancellation-info">
                  <h3 className="font-heading text-lg mb-3 text-gray-700">Prenotazione Annullata</h3>
                  
                  {/* Show refund info if deposit was paid */}
                  {booking.deposit_amount > 0 && (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                        <span className="text-gray-600">Caparra versata:</span>
                        <span className="font-medium">€{booking.deposit_amount?.toFixed(2)}</span>
                      </div>
                      
                      {/* Refund Status - Clear messaging based on new fields */}
                      {booking.refund_decision === 'refund' && (
                        <div className={`p-4 rounded ${
                          booking.refund_status === 'succeeded' ? 'bg-green-50 border border-green-200' :
                          booking.refund_status === 'pending' ? 'bg-blue-50 border border-blue-200' :
                          booking.refund_status === 'failed' ? 'bg-red-50 border border-red-200' :
                          'bg-gray-100'
                        }`}>
                          {booking.refund_status === 'succeeded' && (
                            <>
                              <div className="font-medium text-green-700">✓ Caparra Rimborsata</div>
                              <p className="text-sm text-green-600 mt-1">
                                Il rimborso di €{booking.refund_amount?.toFixed(2) || booking.deposit_amount?.toFixed(2)} è stato completato.
                              </p>
                              {booking.refunded_at && (
                                <p className="text-xs text-green-500 mt-1">
                                  Data rimborso: {new Date(booking.refunded_at).toLocaleString('it-IT')}
                                </p>
                              )}
                            </>
                          )}
                          {booking.refund_status === 'pending' && (
                            <>
                              <div className="font-medium text-blue-700">⏳ Rimborso in Corso</div>
                              <p className="text-sm text-blue-600 mt-1">
                                Il rimborso di €{booking.deposit_amount?.toFixed(2)} è in elaborazione. Riceverai l&apos;accredito entro 5-10 giorni lavorativi.
                              </p>
                            </>
                          )}
                          {booking.refund_status === 'failed' && (
                            <>
                              <div className="font-medium text-red-700">Rimborso Non Riuscito</div>
                              <p className="text-sm text-red-600 mt-1">
                                Si è verificato un problema con il rimborso. Ti contatteremo per risolvere la situazione.
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      
                      {booking.refund_decision === 'keep' && (
                        <div className="p-4 rounded bg-orange-50 border border-orange-200">
                          <div className="font-medium text-orange-700">Caparra Trattenuta</div>
                          <p className="text-sm text-orange-600 mt-1">
                            In base alla politica di cancellazione, la caparra non è rimborsabile.
                          </p>
                        </div>
                      )}
                      
                      {/* Legacy support: if new fields not present, use old refund_authorized */}
                      {!booking.refund_decision && booking.refund_authorized !== null && booking.refund_authorized !== undefined && (
                        <div className={`p-4 rounded ${booking.refund_authorized ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                          {booking.refund_authorized ? (
                            <>
                              <div className="font-medium text-green-700">Rimborso Autorizzato</div>
                              <p className="text-sm text-green-600 mt-1">
                                {booking.refund_processed 
                                  ? 'Il rimborso è stato elaborato. Riceverai l&apos;accredito entro 5-10 giorni lavorativi.'
                                  : 'Il rimborso è in elaborazione.'}
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="font-medium text-orange-700">Caparra Trattenuta</div>
                              <p className="text-sm text-orange-600 mt-1">
                                In base alla politica di cancellazione, la caparra non è rimborsabile.
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* If no deposit was paid */}
                  {(!booking.deposit_amount || booking.deposit_amount === 0) && (
                    <p className="text-sm text-gray-600">
                      La tua prenotazione è stata annullata. Non era stata versata alcuna caparra.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {!hasPendingRequest && (canModify || canCancel) && (
              <div className="flex flex-col sm:flex-row gap-4">
                {canModify && (
                  <Button variant="outline" className="flex-1" onClick={() => setShowModifyModal(true)}>
                    <Edit3 size={16} className="mr-2" /> Richiedi Modifica Data
                  </Button>
                )}
                {canCancel && (
                  <Button variant="outline" className="flex-1 text-destructive border-destructive hover:bg-destructive/10" onClick={() => setShowCancelModal(true)}>
                    <X size={16} className="mr-2" /> Richiedi Cancellazione
                  </Button>
                )}
              </div>
            )}

            <Button variant="ghost" className="mt-6" onClick={() => { setBooking(null); setSearched(false); }}>
              ← Cerca un&apos;altra prenotazione
            </Button>
          </motion.div>
        )}

        {/* Cancellation Modal */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('cancellation_request_title', 'Richiedi Cancellazione')}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Cancellation Policy - from CMS */}
              <div className="p-4 bg-muted/50 rounded border text-sm">
                <h4 className="font-medium mb-2">{t('cancellation_policy_title', 'Politica di Cancellazione')}</h4>
                <div className="text-muted-foreground whitespace-pre-line">
                  {t('cancellation_policy_text', `La cancellazione è sempre possibile contattandoci. 
- Cancellazione oltre 30 giorni prima: rimborso completo della caparra
- Cancellazione 15-30 giorni prima: rimborso del 50% della caparra
- Cancellazione sotto i 15 giorni: caparra non rimborsabile
La modifica della data è gratuita se richiesta con almeno 7 giorni di anticipo, soggetta a disponibilità.`)}
                </div>
              </div>

              <div className="p-4 bg-yellow-50 text-yellow-800 rounded text-sm">
                <AlertTriangle size={16} className="inline mr-2" />
                {t('cancellation_warning', 'La cancellazione è soggetta ad approvazione. Il rimborso della caparra dipende dalle politiche sopra indicate.')}
              </div>

              <div>
                <Label className="mb-2 block">{t('cancellation_reason_label', 'Motivo della cancellazione')} *</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={t('cancellation_reason_placeholder', 'Spiega brevemente il motivo...')}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCancelModal(false)}>{t('btn_cancel', 'Annulla')}</Button>
              <Button variant="destructive" onClick={handleCancellationRequest} disabled={submitting}>
                {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                {t('btn_send_request', 'Invia Richiesta')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modification Modal */}
        <Dialog open={showModifyModal} onOpenChange={setShowModifyModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Richiedi Modifica Data</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label className="mb-2 block">Nuova Data</Label>
                <div className="flex justify-center">
                  <CalendarComponent
                    mode="single"
                    selected={modifyData.new_date}
                    onSelect={(date) => setModifyData(d => ({ ...d, new_date: date }))}
                    disabled={isDateDisabled}
                    locale={it}
                    className="rounded-md border"
                  />
                </div>
                {modifyData.new_date && (
                  <p className="text-center mt-2 text-sm text-muted-foreground">
                    Nuova data: <strong>{format(modifyData.new_date, 'dd MMMM yyyy', { locale: it })}</strong>
                  </p>
                )}
              </div>
              {availability?.settings?.time_slots?.length > 0 && (
                <div>
                  <Label className="mb-2 block">Nuova Fascia Oraria</Label>
                  <select
                    value={modifyData.new_time_slot}
                    onChange={(e) => setModifyData(d => ({ ...d, new_time_slot: e.target.value }))}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Mantieni attuale</option>
                    {availability.settings.time_slots.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label className="mb-2 block">Note (opzionale)</Label>
                <Textarea
                  value={modifyData.reason}
                  onChange={(e) => setModifyData(d => ({ ...d, reason: e.target.value }))}
                  placeholder="Motivazione della modifica..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModifyModal(false)}>Annulla</Button>
              <Button onClick={handleModificationRequest} disabled={submitting}>
                {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                Invia Richiesta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
